import React, { useEffect, useState } from "react";
import { useCartDrawerPanel } from "@/hooks/useCartDrawerPanel";
import { Button, ShimmerEffect } from "@/design-system/Atoms";
import { Typography } from "@/design-system/Atoms";
import { useCartService } from "@/rtkQuery/features/hooks/useCartService";
import type { CartItem } from "@/rtkQuery/features/models/cart.model";
import { AppErrorState, AppEmptyState } from "@/design-system/Feedbacks";
import { getProductImagePath } from "@/utils/getProductImagePath"; 
import { useHandleCheckout } from "@/hooks/useHandleCheckout"; 

const CartSlider: React.FC = () => { 
  const { handleCheckoutClick } = useHandleCheckout();
  const {
    isCartOpen = false,
    toggleCart = () => {},
    itemQuantities = {},
  } = useCartDrawerPanel() || {};

  const {
    cartData,
    cartIsLoading,
    cartIsError,
    cartErrorMessage,
    cartRefetch,
  } = useCartService() || {};
 
  const items: CartItem[] = Array.isArray(cartData?.products)
    ? cartData.products
    : [];
  const dummyImage = "/images/trimix/image-not-found.png";

  const safeToNumber = (value: any): number => {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  const formatCurrency = (value: any): string => {
    const numValue = safeToNumber(value);
    return numValue.toFixed(2);
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isCartOpen) { 
        toggleCart();
        document.activeElement instanceof HTMLElement &&
          document.activeElement.blur();
      }
    };
    if (isCartOpen) {
      document.body.style.overflow = "hidden";
      if (typeof cartRefetch === "function") {
        cartRefetch();
      }
    document.addEventListener("keydown", handleEscape);
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isCartOpen, cartRefetch]);

  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const onCheckoutClick = () => {
    if (isCheckingOut) return;
    setIsCheckingOut(true);

    handleCheckoutClick({
      shouldToggleCart: true,
      toggleCart,
      onFinish: () => setIsCheckingOut(false),
    });
  };

  return (
    <>
      {isCartOpen && (
        <div
          className="fixed inset-0 z-[9998] flex justify-end"
          id="cart-slider"
          onClick={() => toggleCart()}
        >
          <div className="fixed h-full min-h-screen inset-0 bg-[rgba(15,23,42,0.2)] backdrop-brightness-95" />
          <div
            className="relative w-[37rem] h-full min-h-screen bg-white shadow-lg z-[9999] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between bg-primary p-[1.875rem]">
              <Typography
                variant="sectionDescriptionSmaller"
                as="p"
                className="text-white font-normal leading-[1] mt-0 mb-0"
              >
                Cart
              </Typography>
              <button
                onClick={() => toggleCart()}
                type="button"
                aria-label="Close"
                className="cursor-pointer relative cart-btn-close w-8 h-8 flex items-center justify-center rounded-b-lg opacity-50 hover:opacity-90 focus:outline-none focus:rounded-[1.5rem] focus:ring-8 focus:ring-[rgba(24,26,32,0.25)] focus:ring-offset-2 focus:ring-offset-gray-300"
              ></button>
            </div>

            {cartIsLoading ? (
              <div className="w-full p-6 rounded-2xl backdrop-blur-md">
                <ShimmerEffect className="h-[88px] w-[540px] mt-5" />
                <ShimmerEffect className="h-[88px] w-[540px] my-5" />
                <ShimmerEffect className="h-[88px] w-[540px]" />
              </div>
            ) : !cartIsLoading && cartIsError ? (
              <div className="flex-1 p-0 pt-4 pb-4 bg-white">
                <div className="rounded-[20px] h-[100vh] flex items-center justify-center">
                  <AppErrorState
                    message={cartErrorMessage || "Failed to load cart data."}
                    onRetry={() =>
                      typeof cartRefetch === "function" && cartRefetch()
                    }
                  />
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-0 pt-4 pb-4 bg-white">
                {(!cartIsLoading && !Array.isArray(items)) ||
                items.length === 0 ? (
                  <div className="max-w-[36vw] mx-auto px-4">
                    <div className="rounded-[20px] h-[100vh] flex items-center justify-center">
                      <AppEmptyState message="Your cart is empty." />
                    </div>
                  </div>
                ) : (
                  items.map((item: CartItem) => (
                    <div
                      key={item.product_id}
                      id={item.sku}
                      className="flex items-center p-5 gap-3"
                    >
                      <img
                        src={
                          item.Image || item.image
                            ? `/images/products/${item.Image || item.image}`
                            : getProductImagePath(item.product_id)
                        }
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = dummyImage;
                        }}
                        className="me-2 w-[88px] h-[88px] object-contain"
                        alt={item.name}
                      />
                      <div className="flex items-center justify-between w-full flex-wrap gap-0.5 ">
                        <Typography
                          variant="sectionDescriptionSmall"
                          as="p"
                          className="mb-0"
                        >
                          {item.name}
                        </Typography>
                        <div className="flex items-center w-full max-w-[6rem] px-2.5 py-1 rounded-full border">
                          <span className="text-lg text-center w-full">
                            Qty: {itemQuantities[item.product_id] ?? item.qty}
                          </span>
                        </div>
                        <p className="m-0 text-[1.25rem]">
                          ${formatCurrency(item.price)}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {cartIsLoading ? (
              <div className="w-full p-6 rounded-2xl mt-auto backdrop-blur-md">
                <ShimmerEffect className="h-[30px] w-[540px] rounded-[8px]" />
                <ShimmerEffect className="h-[65.33px] w-[540px] rounded-[8px] mt-5" />
              </div>
            ) : (
              items.length > 0 && (
                <div className="bg-[var(--bg-light-rgb)] p-5 mt-auto">
                  <Typography
                    variant="sectionDescriptionSmall"
                    as="p"
                    className="flex justify-between leading-6 mb-4"
                  >
                    Subtotal:{" "}
                    <span>${Number(cartData?.subtotal ?? "0").toFixed(2)}</span>
                  </Typography>
                  <div className="flex flex-wrap gap-y-0">
                    <div className="w-full">
                      <Button
                        variant="darkSubmitButton"
                        size="xl"
                        className="!w-full"
                        type="submit"
                        onClick={onCheckoutClick}
                        disabled={isCheckingOut}
                      >
                        Checkout
                      </Button>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CartSlider;
